import { useState } from "react";
import { C, S } from "../customers/theme";
import { CN_STATUSES, CREDIT_REASONS } from "../lib/creditNotes";
import { downloadCreditNotePDF } from "../lib/creditNotePdf";
import { getBusinessSettings } from "../lib/businessSettings";

const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const n3 = (x) => Math.round((Number(x) || 0) * 1000) / 1000;
const statusMeta = (v) => CN_STATUSES.find((s) => s.value === v) || { label: v, color: C.textDim };
const reasonLabel = (v) => CREDIT_REASONS.find((r) => r.value === v)?.label || (v || "—");

export default function CreditNoteView({ creditNote, busy, onClose, onIssue, onCancel }) {
  const cn = creditNote;
  const lines = (cn.credit_note_items || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const bands = cn.vat_breakdown || [];
  const cur = "€";
  const isDraft = cn.status === "draft";
  const s = statusMeta(cn.status);
  const [dl, setDl] = useState(false);
  const download = async () => {
    setDl(true);
    try { const settings = await getBusinessSettings().catch(() => ({})); downloadCreditNotePDF(cn, settings); }
    finally { setDl(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 820 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{cn.credit_note_number || "New Credit Note"}</h2>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 3 }}>
              {cn.customer_name || "—"}
              {cn.invoices?.invoice_number && <span> · against {cn.invoices.invoice_number}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <span style={S.pill(s.color)}>{s.label}</span>
            {isDraft && <button onClick={() => onIssue(cn)} style={S.btn()} disabled={busy}>Issue</button>}
            {isDraft && <button onClick={() => onCancel(cn)} style={S.btn("ghost")} disabled={busy}>Cancel draft</button>}
            <button onClick={download} style={S.btn("secondary")} disabled={dl}>{dl ? "…" : "⬇ Download PDF"}</button>
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, marginTop: 14 }}>
          <div>
            <div style={S.sectionTitle}>Credit note</div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: C.textMuted }}>
              <div>Issued: <span style={{ color: C.text }}>{cn.issue_date || "—"}</span></div>
              <div>Reason: <span style={{ color: C.text }}>{reasonLabel(cn.reason)}</span></div>
            </div>
          </div>
          <div>
            <div style={S.sectionTitle}>Against invoice</div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: C.textMuted }}>
              <div><span style={{ color: C.text }}>{cn.invoices?.invoice_number || "—"}</span></div>
              {cn.invoices?.total_amount != null && <div>Invoice total: <span style={{ color: C.text }}>{money(cn.invoices.total_amount, cur)}</span></div>}
            </div>
          </div>
          <div>
            <div style={S.sectionTitle}>Customer</div>
            <div style={{ fontSize: 13, lineHeight: 1.7 }}><strong>{cn.customer_name || "—"}</strong></div>
          </div>
        </div>

        <div style={S.sectionTitle}>Credited Items</div>
        <table style={S.table}>
          <thead><tr>
            {["Description", "SKU", "Qty", "Unit Price", "Disc%", "VAT%", "Restock", "Line Total"].map((h) => <th key={h} style={S.th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {lines.length === 0 ? <tr><td colSpan={8} style={S.empty}>No lines</td></tr> : lines.map((l) => (
              <tr key={l.id}>
                <td style={S.td}>{l.description || "—"}</td>
                <td style={S.td}>{l.sku || "—"}</td>
                <td style={S.td}>{n3(l.quantity)}</td>
                <td style={S.td}>{money(l.unit_price, cur)}</td>
                <td style={S.td}>{n3(l.discount_percent)}</td>
                <td style={S.td}>{n3(l.vat_rate)}</td>
                <td style={S.td}>{l.restock ? <span title="Returned to stock" style={{ color: C.success }}>♻︎ Yes</span> : (l.product_id ? "No" : <span style={{ color: C.textDim }}>—</span>)}</td>
                <td style={S.td}>{money(l.line_total, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <div style={{ background: C.surface2, borderRadius: 8, padding: 14, minWidth: 260 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(cn.subtotal, cur)}</strong></div>
            {cn.total_discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Discount</span><strong>−{money(cn.total_discount, cur)}</strong></div>}
            {bands.map((b) => (
              <div key={b.rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12.5 }}><span style={{ color: C.textMuted }}>VAT @ {b.rate}%</span><span>{money(b.vat, cur)}</span></div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Total VAT</span><strong>{money(cn.total_vat, cur)}</strong></div>
            {cn.total_drs > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>DRS deposit</span><strong>{money(cn.total_drs, cur)}</strong></div>}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Credit total</span><span>{money(cn.total_amount, cur)}</span></div>
          </div>
        </div>

        {cn.notes && (<><div style={S.sectionTitle}>Notes</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{cn.notes}</div></>)}
        {isDraft && <div style={{ marginTop: 14, fontSize: 12.5, color: C.textMuted }}>This is a <strong>draft</strong> — it does not affect the invoice balance or stock until you Issue it.</div>}
      </div>
    </div>
  );
}
